// src/components/UserManagement/UsersPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, Space } from 'antd';
import type { FilterValue, TablePaginationConfig } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import './UsersPage.less';
import { getRequestWithParams } from '../../api/httpRequest';

export interface User {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string | null;
  snils: string;
  idE: string;
  roles: string[];
}

type TableFilters = Record<string, FilterValue | null>;

// ===== Component =====
const roleOptions = [
  { label: 'ADMIN', value: 'ADMIN' },
  { label: 'USER', value: 'USER' },
  { label: 'OTHER', value: 'OTHER' },
  { label: 'TEACHER', value: 'TEACHER' },
  { label: 'EMPLOYEE', value: 'EMPLOYEE' },
  { label: 'OLYMPIAD_ADMIN', value: 'OLYMPIAD_ADMIN' },
  { label: 'OLYMPIAD_CURATOR', value: 'OLYMPIAD_CURATOR' },
];

// Соответствие ключа колонки имени параметра фильтра на бекенде
const FILTER_PARAM_BY_KEY: Record<string, string> = {
  id: 'id',
  idE: 'idE',
  fullName: 'fio',
  email: 'email',
  phone: 'phone',
  birthDate: 'birthDate',
  snils: 'snils',
};

const buildQueryParams = (page: number, pageSize: number, filters: TableFilters) => {
  const params: Record<string, unknown> = { page: page - 1, size: pageSize };

  Object.entries(FILTER_PARAM_BY_KEY).forEach(([key, param]) => {
    const value = filters[key];
    const first = Array.isArray(value) && value.length > 0 ? String(value[0]).trim() : '';
    if (first) {
      params[param] = first;
    }
  });

  const roles = filters.roles;
  if (Array.isArray(roles) && roles.length > 0) {
    params.roles = roles.join(',');
  }

  return params;
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState<TableFilters>({});

  const fetchUsers = useCallback(
    async (page: number, pageSize: number, activeFilters: TableFilters) => {
      setLoading(true);
      getRequestWithParams('/server/users', buildQueryParams(page, pageSize, activeFilters))
        .then((resp) => {
          setUsers(resp.data.content);
          setPagination((prev) => ({ ...prev, total: resp.data.totalElements }));
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, filters);
  }, [fetchUsers, pagination.current, pagination.pageSize, filters]);

  const handleTableChange = (
    nextPagination: TablePaginationConfig,
    nextFilters: TableFilters
  ) => {
    const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(filters);
    setFilters(nextFilters);
    setPagination((prev) => ({
      ...prev,
      // при изменении фильтров возвращаемся на первую страницу отфильтрованного списка
      current: filtersChanged ? 1 : nextPagination.current ?? prev.current,
      pageSize: nextPagination.pageSize ?? prev.pageSize,
    }));
  };

  // Текстовый фильтр колонки. Значение отправляется на бекенд через onChange таблицы,
  // поэтому клиентский onFilter не задаётся — фильтрация выполняется на сервере.
  const createTextFilter = (placeholder = 'Поиск...') => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Найти
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Сброс
          </Button>
        </Space>
      </div>
    ),
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      filteredValue: filters.id ?? null,
      ...createTextFilter(),
    },
    {
      title: 'ID_E',
      dataIndex: 'idE',
      key: 'idE',
      filteredValue: filters.idE ?? null,
      ...createTextFilter(),
    },
    {
      title: 'ФИО',
      key: 'fullName',
      render: (record: User) => `${record.lastName} ${record.firstName} ${record.middleName}`,
      filteredValue: filters.fullName ?? null,
      ...createTextFilter(),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      filteredValue: filters.email ?? null,
      ...createTextFilter(),
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      filteredValue: filters.phone ?? null,
      ...createTextFilter(),
    },
    {
      title: 'Дата рождения',
      dataIndex: 'birthDate',
      key: 'birthDate',
      render: (date: string | null) =>
        date && date !== 'null' ? dayjs(date).format('DD.MM.YYYY') : '—',
      filteredValue: filters.birthDate ?? null,
      ...createTextFilter('Поиск (DD.MM.YYYY)...'),
    },
    {
      title: 'СНИЛС',
      dataIndex: 'snils',
      key: 'snils',
      filteredValue: filters.snils ?? null,
      ...createTextFilter(),
    },
    {
      title: 'Роли',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => roles.join(', '),
      filters: roleOptions.map((opt) => ({ text: opt.label, value: opt.value })),
      filterMultiple: true,
      filterSearch: true,
      filteredValue: filters.roles ?? null,
    },
  ];

  return (
    <div className="user-management">
      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          align: "start",
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Всего: ${total}`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />
    </div>
  );
};

export default UsersPage;
